import { useState, useCallback, useMemo, useSyncExternalStore } from 'react';
import { useGeoLocation } from './useGeoLocation';

export interface CookieConsentType {
  necessary: boolean;
  analytics: boolean;
  preferences: boolean;
}
export const defaultConsent: CookieConsentType = { necessary: true, analytics: false, preferences: false };
export const acceptedConsent: CookieConsentType = { necessary: true, analytics: true, preferences: true };
export const COOKIE_NOTICE_VERSION = '2026-10-24';
const CONSENT_COOKIE_MAX_AGE = 365 * 24 * 60 * 60;
const CONSENT_COOKIE_DOMAINS = ['.simplysend.email', '.simplysend.dev', '.simplysend.ai'];
const DEFAULT_RESTRICTED_COUNTRIES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT',
  'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  'GB', 'IS', 'LI', 'NO', 'CH',
];
const DEFAULT_CCPA_REGIONS = ['US-CA'];
export type ConsentMode = 'gdpr' | 'ccpa' | 'none';

function getConsentCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  try {
    const value = document.cookie.split('; ').find(cookie => cookie.startsWith(`${encodeURIComponent(name)}=`));
    return value ? decodeURIComponent(value.slice(value.indexOf('=') + 1)) : null;
  } catch { return null; }
}
function subscribe(listener: () => void) {
  window.addEventListener('consentSaved', listener);
  window.addEventListener('focus', listener);
  window.addEventListener('pageshow', listener);
  return () => {
    window.removeEventListener('consentSaved', listener);
    window.removeEventListener('focus', listener);
    window.removeEventListener('pageshow', listener);
  };
}
function readSavedConsent(raw: string | null): CookieConsentType | null {
  if (!raw) return null;
  try {
    const saved = JSON.parse(raw);
    const timestamp = Date.parse(saved.recordedAt);
    // A previous refusal remains a refusal; old grants never become current consent.
    if (saved.noticeVersion !== COOKIE_NOTICE_VERSION && saved.analytics === false) return defaultConsent;
    if (saved.noticeVersion !== COOKIE_NOTICE_VERSION || !Number.isFinite(timestamp)
      || timestamp > Date.now() || Date.now() - timestamp >= CONSENT_COOKIE_MAX_AGE * 1000
      || typeof saved.analytics !== 'boolean' || typeof saved.preferences !== 'boolean') return null;
    return { necessary: true, analytics: saved.analytics, preferences: saved.preferences };
  } catch { return null; }
}
interface UseConsentManagerProps {
  cookieKey?: string;
  restrictedCountries?: string[];
  ccpaRegions?: string[];
}
export const useConsentManager = ({ cookieKey = 'cookie_consent', restrictedCountries = DEFAULT_RESTRICTED_COUNTRIES, ccpaRegions = DEFAULT_CCPA_REGIONS }: UseConsentManagerProps = {}) => {
  const raw = useSyncExternalStore(subscribe, () => getConsentCookie(cookieKey), () => null);
  const savedConsent = useMemo(() => readSavedConsent(raw), [raw]);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const { countryCode, regionCode, loading } = useGeoLocation();
  const knownCountry = !!countryCode && /^[A-Z]{2}$/.test(countryCode) && countryCode !== 'XX';
  const consentMode: ConsentMode = knownCountry && restrictedCountries.includes(countryCode!) ? 'gdpr'
    : knownCountry && regionCode && ccpaRegions.includes(`${countryCode}-${regionCode}`) ? 'ccpa' : 'none';
  // Unknown location never becomes implicit permission and never causes a global banner.
  const consent = savedConsent || (knownCountry && consentMode !== 'gdpr' ? acceptedConsent : defaultConsent);
  const showBanner = !savedConsent && !loading && consentMode !== 'none' && !bannerDismissed;
  const saveConsent = useCallback((choice: CookieConsentType) => {
    const record = { necessary: true, analytics: choice.analytics === true, preferences: choice.preferences === true,
      noticeVersion: COOKIE_NOTICE_VERSION, recordedAt: new Date().toISOString(), countryCode, regionCode };
    const domain = CONSENT_COOKIE_DOMAINS.find(value => window.location.hostname === value.slice(1) || window.location.hostname.endsWith(value));
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${encodeURIComponent(cookieKey)}=${encodeURIComponent(JSON.stringify(record))}; Path=/; Max-Age=${CONSENT_COOKIE_MAX_AGE}; SameSite=Lax${domain ? `; Domain=${domain}` : ''}${secure}`;
    setBannerDismissed(true);
    window.dispatchEvent(new Event('consentSaved'));
  }, [cookieKey, countryCode, regionCode]);
  const setShowBanner = useCallback((show: boolean) => setBannerDismissed(!show), []);
  return { consent, showBanner, setShowBanner, saveConsent, isInitialized: !loading, countryCode, consentMode, hasSavedConsent: savedConsent !== null };
};
